/**
 * Multi-pane split view — 2x2 quadrants, each running its own command
 * independently. Bloomberg/Refinitiv-style "4-up" layout.
 *
 * Features:
 *  • 4 panes, each with its own command input
 *  • Type a command into a pane → executes and shows output
 *  • Layout persists to localStorage so restart keeps your setup
 *  • Click any pane header to take the full screen (toggle)
 *  • Plus/minus to swap between 1/2/4 pane layouts
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { clsx } from 'clsx'
import { Grid2X2, Maximize2, Minimize2, Play, RefreshCw, X } from 'lucide-react'
import { api } from '@/lib/api'
import type { TerminalResponse } from '@/lib/types'

const LOCALSTORAGE_KEY = 'terminal_multipane_v1'
const LAYOUTS_KEY = 'terminal_multipane_layouts_v1'
const MAX_PANES = 4
// Commands that should auto-refresh when pinned to a pane. Everything
// else is run-once until the user hits refresh manually — saves API
// calls on stuff that doesn't tick (like /dcf).
const AUTO_REFRESH_COMMANDS = /^\/(portfolio|positions|pnl|regime|watch|indices|quote|chart|forex|crypto|vix|movers|sectors|pf|pos|q|c|idx|fx|vol|signals|market|mov|heatmap)\b/i
const AUTO_REFRESH_MS = 60_000  // 60s cadence — cheap enough for our rate limits

interface Pane {
  id: number
  command: string    // last-executed command (empty = blank pane)
  editing: string    // currently-typed command waiting for Enter
}

const DEFAULT_PANES: Pane[] = [
  { id: 1, command: '/portfolio',  editing: '' },
  { id: 2, command: '/blotter',    editing: '' },
  { id: 3, command: '/regime',     editing: '' },
  { id: 4, command: '/news SPY',   editing: '' },
]

function loadPanes(): Pane[] {
  try {
    const raw = localStorage.getItem(LOCALSTORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw)
      if (Array.isArray(parsed) && parsed.length === MAX_PANES) return parsed
    }
  } catch {}
  return DEFAULT_PANES
}

function savePanes(panes: Pane[]) {
  try { localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(panes)) } catch {}
}

/** Named layout library — each entry stores the 4 commands by name. */
function loadLayouts(): Record<string, string[]> {
  try {
    const raw = localStorage.getItem(LAYOUTS_KEY)
    if (raw) {
      const parsed = JSON.parse(raw)
      if (parsed && typeof parsed === 'object') return parsed
    }
  } catch {}
  return {}
}

function saveLayouts(map: Record<string, string[]>) {
  try { localStorage.setItem(LAYOUTS_KEY, JSON.stringify(map)) } catch {}
}

interface Props {
  open: boolean
  onClose: () => void
}

export function MultiPaneView({ open, onClose }: Props) {
  const [panes, setPanes] = useState<Pane[]>(loadPanes)
  const [results, setResults] = useState<Record<number, TerminalResponse | null>>({})
  const [loading, setLoading] = useState<Record<number, boolean>>({})
  const [errors, setErrors] = useState<Record<number, string | null>>({})
  const [maximisedId, setMaximisedId] = useState<number | null>(null)
  const [layouts, setLayouts] = useState<Record<string, string[]>>(loadLayouts)
  const [layoutsOpen, setLayoutsOpen] = useState(false)
  const refreshTimers = useRef<Record<number, ReturnType<typeof setInterval> | null>>({})

  const saveCurrentAs = useCallback(() => {
    const name = window.prompt('Save layout as: (e.g. "morning", "earnings", "crypto")')
    if (!name) return
    const clean = name.trim().slice(0, 32)
    if (!clean) return
    const next = { ...layouts, [clean]: panes.map(p => p.command) }
    setLayouts(next)
    saveLayouts(next)
    setLayoutsOpen(false)
  }, [layouts, panes])

  const loadLayout = useCallback((name: string) => {
    const cmds = layouts[name]
    if (!cmds) return
    const nextPanes = cmds.slice(0, MAX_PANES).map((cmd, i) => ({
      id: i + 1,
      command: cmd,
      editing: '',
    }))
    while (nextPanes.length < MAX_PANES) {
      nextPanes.push({ id: nextPanes.length + 1, command: '', editing: '' })
    }
    setPanes(nextPanes)
    setResults({})
    setLayoutsOpen(false)
  }, [layouts])

  const removeLayout = useCallback((name: string) => {
    if (!window.confirm(`Delete layout "${name}"?`)) return
    const next = { ...layouts }
    delete next[name]
    setLayouts(next)
    saveLayouts(next)
  }, [layouts])

  // Execute a pane's command
  const execute = useCallback(async (paneId: number, commandOverride?: string) => {
    const pane = panes.find(p => p.id === paneId)
    if (!pane) return
    const cmd = (commandOverride ?? pane.command).trim()
    if (!cmd) return

    setLoading(s => ({ ...s, [paneId]: true }))
    setErrors(s => ({ ...s, [paneId]: null }))
    try {
      const { data } = await api.post<TerminalResponse>('/api/terminal/command', { command: cmd })
      setResults(s => ({ ...s, [paneId]: data }))
    } catch (e: any) {
      setErrors(s => ({ ...s, [paneId]: e?.response?.data?.detail ?? e?.message ?? 'Failed' }))
    } finally {
      setLoading(s => ({ ...s, [paneId]: false }))
    }
  }, [panes])

  // Auto-execute each pane's command on open
  useEffect(() => {
    if (!open) return
    for (const p of panes) {
      if (p.command && !(p.id in results)) {
        execute(p.id)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // Auto-refresh for "live" commands (/portfolio, /quote, /regime, ...)
  // every AUTO_REFRESH_MS. Manual-only commands (/dcf, /tearsheet, …)
  // stay as-run until the user hits the refresh button.
  useEffect(() => {
    // Clear any existing timers on pane change / close
    for (const id of Object.keys(refreshTimers.current)) {
      const t = refreshTimers.current[Number(id)]
      if (t) clearInterval(t)
    }
    refreshTimers.current = {}
    if (!open) return
    for (const p of panes) {
      if (p.command && AUTO_REFRESH_COMMANDS.test(p.command)) {
        refreshTimers.current[p.id] = setInterval(() => {
          execute(p.id)
        }, AUTO_REFRESH_MS)
      }
    }
    return () => {
      for (const id of Object.keys(refreshTimers.current)) {
        const t = refreshTimers.current[Number(id)]
        if (t) clearInterval(t)
      }
      refreshTimers.current = {}
    }
  }, [open, panes, execute])

  // Persist panes on change
  useEffect(() => { savePanes(panes) }, [panes])

  const runEditing = useCallback((paneId: number) => {
    const pane = panes.find(p => p.id === paneId)
    if (!pane) return
    const next = pane.editing.trim()
    if (!next) return
    const cmd = next.startsWith('/') ? next : '/' + next
    setPanes(ps => ps.map(p => p.id === paneId ? { ...p, command: cmd, editing: '' } : p))
    execute(paneId, cmd)
  }, [panes, execute])

  // Visible panes based on maximise state
  const visible = useMemo(() => {
    if (maximisedId !== null) {
      const p = panes.find(x => x.id === maximisedId)
      return p ? [p] : panes
    }
    return panes
  }, [panes, maximisedId])

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-40 bg-black/95 backdrop-blur flex flex-col"
      role="dialog"
      aria-modal="true"
      aria-label="Multi-pane terminal"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-crt-green/30 bg-black/80">
        <div className="flex items-center gap-2">
          <Grid2X2 size={14} className="text-crt-amber" />
          <span className="font-mono text-xs text-crt-green font-bold">MULTI-PANE · 2×2</span>
          <span className="text-[10px] text-crt-dim font-mono">
            {maximisedId !== null ? `pane ${maximisedId} maximised` : '4 independent command panes'}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[10px] text-crt-dim font-mono">
            <kbd>Enter</kbd> run · <kbd>Esc</kbd> close
          </span>
          {/* Saved-layout switcher */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setLayoutsOpen(o => !o)}
              className="text-crt-dim hover:text-crt-green font-mono text-xs flex items-center gap-1"
            >
              Layouts {Object.keys(layouts).length > 0 && <span className="text-crt-dim">({Object.keys(layouts).length})</span>}
            </button>
            {layoutsOpen && (
              <>
                <button
                  type="button"
                  aria-label="Close layouts menu"
                  onClick={() => setLayoutsOpen(false)}
                  className="fixed inset-0 z-40 cursor-default"
                />
                <div className="absolute right-0 top-full mt-1 z-50 min-w-[220px] bg-black border border-crt-green/30 rounded font-mono text-xs shadow-xl">
                  <button
                    type="button"
                    onClick={saveCurrentAs}
                    className="w-full text-left px-3 py-2 text-crt-cyan hover:bg-crt-green/10 border-b border-crt-green/20"
                  >
                    + Save current panes as…
                  </button>
                  {Object.keys(layouts).length === 0 ? (
                    <div className="px-3 py-2 text-crt-dim">no saved layouts</div>
                  ) : (
                    Object.keys(layouts).sort().map(name => (
                      <div key={name} className="flex items-center hover:bg-crt-green/5">
                        <button
                          type="button"
                          onClick={() => loadLayout(name)}
                          className="flex-1 text-left px-3 py-1.5 text-crt-green hover:text-crt-bright truncate"
                          title={`Load: ${layouts[name].join(', ')}`}
                        >
                          {name}
                        </button>
                        <button
                          type="button"
                          onClick={() => removeLayout(name)}
                          className="px-2 py-1.5 text-crt-dim hover:text-red-400"
                          aria-label={`Delete ${name}`}
                        >
                          ×
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </>
            )}
          </div>
          <button type="button" onClick={onClose}
                  className="text-crt-dim hover:text-crt-green font-mono text-xs flex items-center gap-1">
            <X size={14} /> close
          </button>
        </div>
      </div>

      {/* Panes grid */}
      <div className={clsx(
        'flex-1 grid gap-px overflow-hidden bg-crt-green/10',
        maximisedId !== null ? 'grid-cols-1' : 'grid-cols-2 grid-rows-2',
      )}>
        {visible.map(pane => (
          <PaneBody
            key={pane.id}
            pane={pane}
            result={results[pane.id] ?? null}
            loading={loading[pane.id] ?? false}
            error={errors[pane.id] ?? null}
            maximised={maximisedId === pane.id}
            onEditingChange={val => setPanes(ps => ps.map(p => p.id === pane.id ? { ...p, editing: val } : p))}
            onRun={() => runEditing(pane.id)}
            onRefresh={() => execute(pane.id)}
            onMaximise={() => setMaximisedId(maximisedId === pane.id ? null : pane.id)}
          />
        ))}
      </div>
    </div>
  )
}

function PaneBody({
  pane, result, loading, error, maximised,
  onEditingChange, onRun, onRefresh, onMaximise,
}: {
  pane: Pane
  result: TerminalResponse | null
  loading: boolean
  error: string | null
  maximised: boolean
  onEditingChange: (val: string) => void
  onRun: () => void
  onRefresh: () => void
  onMaximise: () => void
}) {
  return (
    <div className="flex flex-col bg-black min-h-0 overflow-hidden">
      {/* Pane header */}
      <div className="flex items-center gap-2 px-3 py-1.5 border-b border-crt-green/20 bg-black/70">
        <span className="text-[10px] text-crt-dim font-mono">[{pane.id}]</span>
        <input
          type="text"
          value={pane.editing || pane.command}
          onChange={e => onEditingChange(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') { e.preventDefault(); onRun() }
          }}
          placeholder="/command"
          aria-label={`Pane ${pane.id} command`}
          className="flex-1 bg-transparent text-crt-green font-mono text-xs outline-none placeholder:text-crt-dim/50"
        />
        <button type="button" onClick={onRun} disabled={loading}
                title="Run (Enter)" aria-label="Run command"
                className="text-crt-dim hover:text-crt-green disabled:opacity-30">
          <Play size={12} />
        </button>
        <button type="button" onClick={onRefresh} disabled={loading}
                title="Re-run current command" aria-label="Refresh pane"
                className={clsx('text-crt-dim hover:text-crt-green', loading && 'animate-spin')}>
          <RefreshCw size={12} />
        </button>
        <button type="button" onClick={onMaximise}
                title={maximised ? 'Restore grid' : 'Maximise pane'}
                aria-label={maximised ? 'Restore grid' : 'Maximise pane'}
                className="text-crt-dim hover:text-crt-green">
          {maximised ? <Minimize2 size={12} /> : <Maximize2 size={12} />}
        </button>
      </div>

      {/* Output */}
      <div className="flex-1 overflow-y-auto crt-scrollbar p-3 font-mono text-xs leading-relaxed">
        {loading && !result ? (
          <span className="text-crt-dim">running {pane.command}…</span>
        ) : error ? (
          <span className="text-red-400">error: {error}</span>
        ) : result ? (
          <pre className={clsx(
            'whitespace-pre-wrap',
            result.type === 'error' ? 'text-red-400' : 'text-crt-green',
          )}>
            {/* Strip the colour markup tokens for the pane — simpler
                rendering without bringing in the full RichLine parser */}
            {(result.formatted || '').replace(/§[grabcdep]([^§]+)§/g, '$1')}
          </pre>
        ) : (
          <span className="text-crt-dim">empty — type a command above and press Enter</span>
        )}
      </div>
    </div>
  )
}
